import { signInWithCustomToken } from "firebase/auth";
import { onValue, ref } from "firebase/database";

import config from "../../config";
import { LOGOUT } from "../../constants/actionTypes";
import {
  addPublicUserDetailsFailure,
  addPublicUserDetailsRequest,
  addPublicUserDetailsSuccess,
  authRequest,
  authRequestFailure,
  authRequestSuccess,
  loginUserSuccess,
  pincodeRequest,
  pincodeRequestSuccess,
  receiveAccountRealtimeData,
  receiveUserRealtimeData
} from "../../reducers/auth";
import { fetchPromise } from "../../utils";
import { auth, database } from "../../utils/firebase";

export const loginUser = (username, password) => async dispatch => {
  dispatch(authRequest());
  try {
    const response = await fetchPromise(
      config.api_url + "/auth/login/web",
      "get",
      {
        Authorization: "Basic " + btoa(username + ":" + password)
      }
    );
    await signInWithCustomToken(auth, response.firebaseToken);
    localStorage.setItem("firebaseToken", response.firebaseToken);
    dispatch(loginUserSuccess(response));
  } catch (error) {
    dispatch(authRequestFailure(error));
  }
};

export const resetPasswordRequest = (username, openLink) => async dispatch => {
  dispatch(authRequest());
  try {
    const response = await fetchPromise(
      config.api_url + "/auth/reset-password-request",
      "post",
      {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      { inputData: username }
    );
    dispatch(authRequestSuccess());
    if (response.genericId) {
      return openLink(
        "/auth/verify-otp?generic=" +
          response.genericId +
          "&reset=true&phone=" +
          username
      );
    } else {
      throw new Error("Auth failure");
    }
  } catch {
    dispatch(authRequestFailure(true));
  }
};

export const resetPasswordVerifyOTP =
  (otp, genericId, phone, openLink) => async dispatch => {
    dispatch(authRequest());
    try {
      const response = await fetchPromise(
        config.api_url + "/auth/reset-password-verify-otp",
        "post",
        {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        { genericId, inputData: phone, otp }
      );
      dispatch(authRequestSuccess());
      if (response.resetToken) {
        return openLink("/auth/reset-password?token=" + response.resetToken);
      } else {
        throw new Error("Auth failure");
      }
    } catch (error) {
      dispatch(authRequestFailure(error));
    }
  };

export const resetPassword = (password, token) => async dispatch => {
  dispatch(authRequest());
  try {
    const response = await fetchPromise(
      config.api_url + "/auth/reset-password",
      "post",
      {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      { password, token, medium: "web" }
    );
    localStorage.setItem("firebaseToken", response.firebaseToken);
    dispatch(loginUserSuccess(response));
  } catch {
    dispatch(authRequestFailure(true));
  }
};

export const verifyOTP = (otp, genericId, phone) => async dispatch => {
  dispatch(authRequest());
  try {
    const response = await fetchPromise(
      config.api_url + "/auth/verify-otp",
      "post",
      {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      { genericId, inputData: phone, otp, medium: "web" }
    );
    localStorage.setItem("firebaseToken", response.firebaseToken);
    dispatch(loginUserSuccess(response));
  } catch (error) {
    dispatch(authRequestFailure(error));
  }
};

export const signup =
  (name, username, phone, email, password, openLink) => async dispatch => {
    dispatch(authRequest());
    try {
      const response = await fetchPromise(
        config.api_url + "/auth/signup",
        "post",
        {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        { name, username, phone, email, password }
      );
      dispatch(authRequestSuccess());
      if (response.genericId) {
        return openLink(
          "/verify-otp?generic=" +
            response.genericId +
            "&reset=false&phone=" +
            phone
        );
      } else {
        throw new Error("Auth failure");
      }
    } catch (error) {
      dispatch(authRequestFailure(error));
    }
  };

export const fetchPincodeData = pincode => async dispatch => {
  dispatch(pincodeRequest());
  try {
    const response = await fetchPromise(
      config.api_url + "/admin/resolve-address/" + pincode,
      "get"
    );
    dispatch(pincodeRequestSuccess(response));
  } catch (error) {
    return authRequestFailure(error);
  }
};

export const updateProfile =
  (role, category, pincode, openLink) => async dispatch => {
    dispatch(authRequest());
    try {
      const response = await fetchPromise(
        config.api_url + "/auth/update-profile-web",
        "post",
        {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        { role, category, pincode, medium: "web" }
      );
      localStorage.setItem("firebaseToken", response.firebaseToken);
      dispatch(loginUserSuccess(response));
      if (role == "seller") {
        return openLink("/dashboard/root");
      } else {
        return openLink("/");
      }
    } catch (error) {
      dispatch(authRequestFailure(error));
    }
  };

export const logoutRequest = openLink => async dispatch => {
  await fetchPromise(config.api_url + "/auth/logout/web", "get");
  dispatch({
    type: LOGOUT
  });
  openLink("/", true);
};

export const addPublicUserDetails =
  (pincode, category = "mobile") =>
  async dispatch => {
    dispatch(addPublicUserDetailsRequest());
    const response = await fetchPromise(
      config.api_url + "/public/add-details",
      "post",
      {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      { pincode, category }
    );
    if (response.error) {
      dispatch(addPublicUserDetailsFailure());
    } else {
      dispatch(addPublicUserDetailsSuccess(response));
    }
  };

export const getUserRealtimeData = () => async (dispatch, state) => {
  if (state().auth.isAuthenticated) {
    onValue(ref(database, `userData/${state().auth.user._id}`), data => {
      dispatch(receiveUserRealtimeData(data.val()));
    });
  } else {
    const sessionId = state().auth.session._id;
    const firebaseSessionKey = sessionId.replace(".", "-");
    onValue(ref(database, `sessions/${firebaseSessionKey}`), data => {
      dispatch(receiveUserRealtimeData(data.val()));
    });
  }
};

export const getAccountRealtimeData = () => async (dispatch, state) => {
  if (state().auth.isAuthenticated && state().auth.user.account) {
    onValue(ref(database, `account/${state().auth.user.account}`), data => {
      const finalData = data.val() ? data.val() : {};
      dispatch(receiveAccountRealtimeData(finalData));
    });
  }
};
